import React, {
  Dispatch,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { UserPermissions as UserPermissionsApi } from "../api/index.defs";
import { TableRef } from "../components/Shared/Table/Table";
import { KeyValue } from "../models/key-value";
import { UserPermissions } from "../models/permission";
import Role from "../models/role";
import { IUser } from "../models/users.interface";
import organizationService from "../services/organization.service";
import usersService from "../services/users.service";

interface IManageUsersState {
  loading: boolean;
  users: IUser[];
  selectedUser: IUser | null;
  usersTableRef: TableRef | null;
  emailDomains: KeyValue<string>[] | null;
  addUserDetails: {
    role?: Role;
    entity?: string;
  };
  userOrgPermission: {
    defaults: Partial<UserPermissions>;
    isAllowed: UserPermissions;
    loading: boolean;
  };
}

export enum ManageUsersActionType {
  SET_LOADING = "SET_LOADING",
  SET_USERS = "SET_USERS",
  SET_SELECTED_USER = "SET_SELECTED_USER",
  SET_TABLE_REF = "SET_TABLE_REF",
  SET_EMAIL_DOMAINS = "SET_EMAIL_DOMAINS",
  SET_ADD_USER_DETAILS = "SET_ADD_USER_DETAILS",
  SET_USER_ORGANIZATION_PERMISSIONS = "SET_USER_ORGANIZATION_PERMISSIONS",
  SET_USER_ORGANIZATION_PERMISSIONS_LOADING = "SET_USER_ORGANIZATION_PERMISSIONS_LOADING",
}

type ManageUsersActions = {
  loadUsers: (opts?: { isPatients?: boolean }) => Promise<void>;
};

type ManageUsersReducerAction =
  | {
      type: ManageUsersActionType.SET_LOADING;
      payload: boolean;
    }
  | {
      type: ManageUsersActionType.SET_USERS;
      payload: IUser[];
    }
  | {
      type: ManageUsersActionType.SET_TABLE_REF;
      payload: TableRef;
    }
  | {
      type: ManageUsersActionType.SET_SELECTED_USER;
      payload: IUser | null;
    }
  | {
      type: ManageUsersActionType.SET_EMAIL_DOMAINS;
      payload: KeyValue<string>[] | null;
    }
  | {
      type: ManageUsersActionType.SET_ADD_USER_DETAILS;
      payload: {
        role?: Role;
        entity?: string;
      };
    }
  | {
      type: ManageUsersActionType.SET_USER_ORGANIZATION_PERMISSIONS;
      payload: {
        defaults: Partial<UserPermissions>;
        isAllowed: UserPermissions;
      };
    }
  | {
      type: ManageUsersActionType.SET_USER_ORGANIZATION_PERMISSIONS_LOADING;
      payload: boolean;
    };

interface IManageUsersContext {
  state: IManageUsersState;
  dispatch: Dispatch<ManageUsersReducerAction>;
  actions: ManageUsersActions;
}

const initState: IManageUsersState = {
  loading: false,
  users: [],
  selectedUser: null,
  usersTableRef: null,
  emailDomains: null,
  addUserDetails: {},
  userOrgPermission: {
    defaults: {},
    isAllowed: {
      canApproveEcg: true,
      canEditInterpretation: true,
      canLogIntoPortal: true,
      canManageNonProUsers: true,
      canViewEcgs: true,
      canViewReports: true,
      canViewSelfClinicsEcgs: true,
      canSendEcgsViaMail: true,
      showEcgPreview: true,
      canArchiveEcgs: true,
      isViewSelfClinicIsAllowed: false,
      canManageEntities: true,
    },
    loading: false,
  },
};

const ManageUsersContext = React.createContext<IManageUsersContext>({
  state: initState,
  dispatch: () => {},
  actions: { loadUsers: () => new Promise((resolve) => resolve()) },
});

const manageUsersReducer = (
  state: IManageUsersState,
  action: ManageUsersReducerAction
): IManageUsersState => {
  switch (action.type) {
    case ManageUsersActionType.SET_LOADING:
      return { ...state, loading: action.payload };
    case ManageUsersActionType.SET_USERS:
      return { ...state, users: action.payload };
    case ManageUsersActionType.SET_SELECTED_USER:
      return { ...state, selectedUser: action.payload };
    case ManageUsersActionType.SET_TABLE_REF:
      return { ...state, usersTableRef: action.payload };
    case ManageUsersActionType.SET_EMAIL_DOMAINS:
      return { ...state, emailDomains: action.payload };
    case ManageUsersActionType.SET_ADD_USER_DETAILS:
      return { ...state, addUserDetails: action.payload };
    case ManageUsersActionType.SET_USER_ORGANIZATION_PERMISSIONS:
      return {
        ...state,
        userOrgPermission: { ...state.userOrgPermission, ...action.payload },
      };
    case ManageUsersActionType.SET_USER_ORGANIZATION_PERMISSIONS_LOADING:
      return {
        ...state,
        userOrgPermission: {
          ...state.userOrgPermission,
          loading: action.payload,
        },
      };
    default:
      return state;
  }
};

type ManageUsersContextProviderProps = PropsWithChildren<{}>;

export const ManageUsersContextProvider = ({
  children,
}: ManageUsersContextProviderProps) => {
  const [state, dispatch] = useReducer(manageUsersReducer, initState);

  const actions: ManageUsersActions = {
    loadUsers: async (options) => {
      const { isPatients = false } = options ?? {};
      try {
        const { selectedUser } = state;

        dispatch({
          type: ManageUsersActionType.SET_LOADING,
          payload: true,
        });

        dispatch({
          type: ManageUsersActionType.SET_SELECTED_USER,
          payload: null,
        });

        const users = await usersService.getUsers(
          isPatients
            ? [Role.OrgNonProfessional]
            : [Role.OrgAdmin, Role.OrgProfessional, Role.Reviewer]
        );

        if (!users) {
          return;
        }

        dispatch({
          type: ManageUsersActionType.SET_USERS,
          payload: users,
        });

        if (selectedUser) {
          const user = users.find((u) => u.id === selectedUser.id);
          if (user) {
            dispatch({
              type: ManageUsersActionType.SET_SELECTED_USER,
              payload: user,
            });
          }
        }
      } finally {
        dispatch({
          type: ManageUsersActionType.SET_LOADING,
          payload: false,
        });
      }
    },
  };

  const getOrganiztionDefaultPermissions = async (
    entity: string,
    role: Role
  ) => {
    try {
      dispatch({
        type: ManageUsersActionType.SET_USER_ORGANIZATION_PERMISSIONS_LOADING,
        payload: true,
      });

      const {
        success,
        data,
      } = await organizationService.getOrganiztionDefaultPermissions(
        entity,
        role
      );
      if (!success || !data) {
        return;
      }

      const fetchedPermissions = data.userPermissions ?? {};
      const fetchedDefaultPermissions = (Object.keys(fetchedPermissions).reduce(
        (result: UserPermissionsApi, key: any) => ({
          ...result,
          [key]:
            fetchedPermissions[key as keyof UserPermissionsApi]?.defaultValue,
        }),
        {}
      ) as unknown) as UserPermissions;
      const fetchedIsAllowedPermissions = (Object.keys(
        fetchedPermissions
      ).reduce(
        (result: UserPermissionsApi, key: any) => ({
          ...result,
          [key]:
            fetchedPermissions[key as keyof UserPermissionsApi]
              ?.isPermissionAllowed,
        }),
        {}
      ) as unknown) as UserPermissions;

      dispatch({
        type: ManageUsersActionType.SET_USER_ORGANIZATION_PERMISSIONS,
        payload: {
          defaults: {
            ...state.userOrgPermission.defaults,
            ...fetchedDefaultPermissions,
          },
          isAllowed: {
            ...state.userOrgPermission.isAllowed,
            ...fetchedIsAllowedPermissions,
          },
        },
      });
    } finally {
      dispatch({
        type: ManageUsersActionType.SET_USER_ORGANIZATION_PERMISSIONS_LOADING,
        payload: false,
      });
    }
  };

  useEffect(() => {
    const {
      addUserDetails: { entity, role },
    } = state;
    if (!entity || !role) {
      return;
    }

    getOrganiztionDefaultPermissions(entity, role);
  }, [state.addUserDetails.entity, state.addUserDetails.role]); // eslint-disable-line

  return (
    <ManageUsersContext.Provider value={{ state, dispatch, actions }}>
      {children}
    </ManageUsersContext.Provider>
  );
};

export const useManageUsersContext = () => useContext(ManageUsersContext);
